import Pen from "../../../../assets/icons/edit.svg";
import { useTranslation } from "react-i18next";
import Archive from "../../../../assets/icons/archive.svg";
import { useTokenContext } from "../../../../context/tokenContext";
import useSorting from "../../../../hooks/useSorting";
import Return from "../../../../assets/icons/return.svg";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateFormat,
  splitMissionName,
} from "../../../../services/Utils/functionService";
import { usePartnerMissionStore } from "../../../../stores/Cac/Partner/partnerMission";
import repeat from "../../../../assets/icons/repeat.svg";
import { postMissionRenew, postMissionUnwatch, postMissionWatch } from "../../../../services/Api/customerService";
import {
  showToastError,
  showToastSuccess,
} from "../../../../services/Toasts/toasts";
import Bell from "../../../../assets/icons/bellBlack.svg";
import BellActive from "../../../../assets/icons/bellActive.svg";
import { useState } from "react";

const PartnerMissionTable = () => {
  const { t } = useTranslation();
  // Context
  const { roleId, isSuperAdmin } = useTokenContext();

  // Store
  const {
    missions,
    setMissionId,
    setChoiceModal,
    setRenewable,
    updateMission,
    setUpdateMission,
  } = usePartnerMissionStore();

  // Hooks
  const [sortedData, handleSort] = useSorting(missions);
  const { idPartner, idCustomer } = useParams();
  const navigate = useNavigate();

  // State pour le tri
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Fonction de tri personnalisée
  const handleCustomSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    handleSort(field);
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    }
  };

  // Reconduire la mission
  const handleRenewMission = (idMission: number) => {
    postMissionRenew(Number(idCustomer), idMission)
      .then(() => {
        showToastSuccess(t("partner.renewSuccess"));
        setUpdateMission(!updateMission);
      })
      .catch((error) => {
        if(error.response.data.message === "Can't renew a mission if not all the docRequest are accepted") {
          showToastError(t("partner.acceptRequests"));
        } else {
          showToastError(t("common.error"));
        }
      });
  };

  // Activer les notifications
  const handleWatchMission = (idMission: number, watching: boolean) => {
    if(!watching) {
      postMissionWatch(Number(idCustomer), idMission)
      .then(() => {
        showToastSuccess(t("partner.watchSuccess"));
        setUpdateMission(!updateMission);
      })
      .catch(() => {
        showToastError(t("common.error"));
      });
    } else {
      postMissionUnwatch(Number(idCustomer), idMission)
      .then(() => {
        showToastSuccess(t("partner.unwatchSuccess"));
        setUpdateMission(!updateMission);
      })
      .catch(() => {
        showToastError(t("common.error"));
      });
    }
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Header avec système de tri */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 py-3 sm:px-5 2xl:py-4">
        <div className="hidden md:flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
          <button
            type="button"
            className="group cursor-pointer rounded-xl bg-gray-100 p-2.5 transition-colors duration-200 hover:bg-gray-200 sm:p-3"
            onClick={() => navigate(`/partner/${idPartner}/customers`)
            }
            title={t("common.back")}
          >
            <img src={Return} alt="return" className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          </button>
          {/* Filtres et tri */}
          <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 lg:w-auto lg:pb-0">
            <span className="flex-shrink-0 text-sm font-medium text-gray-600">{t("common.sortBy")}</span>
            
            {/* Bouton de tri par service */}
            <button 
              onClick={() => handleCustomSort("category")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "category" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("common.service")}</span>
              {getSortIcon("category")}
            </button>
            
            {/* Bouton de tri par nom */}
            <button 
              onClick={() => handleCustomSort("name")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "name" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("common.name")}</span>
              {getSortIcon("name")}
            </button>
            
            {/* Bouton de tri par responsable */}
            <button 
              onClick={() => handleCustomSort("fullName")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "fullName" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("common.manager")}</span>
              {getSortIcon("fullName")}
            </button>
            
            {/* Bouton de tri par date */}
            <button 
              onClick={() => handleCustomSort("dueDate")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "dueDate" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("common.dueDate")}</span>
              {getSortIcon("dueDate")}
            </button>
          </div>
        </div>
      </div>

      {/* Table des missions */}
      <div className="min-h-0 w-full flex-1 scroll-pb-20 overflow-y-auto pb-20 sm:scroll-pb-3 sm:pb-3">
        {/* En-têtes de colonnes */}
        <div className="sticky top-0 z-10 hidden w-full grid-cols-9 border-b border-gray-200 bg-gray-50 py-2 text-sm font-semibold text-gray-700 md:grid">
          <div className="col-start-1 col-end-2 grid grid-cols-2 items-center">
            <p className="col-start-1 col-end-2"></p>
            <p className="col-start-2 col-end-3">{t("common.service")}</p>
          </div>
          <div className="col-start-2 col-end-4 flex items-center">
            {t("common.name")}
          </div>
          <div className="col-start-4 col-end-5 flex items-center">
            {t("common.manager")}
          </div>
          <div className="col-start-5 col-end-6 flex items-center">
            {t("common.dueDate")}
          </div>
          <div className="col-start-6 col-end-7 flex items-center">
            {t("partner.archiveDate")}
          </div>
          <div className="col-start-7 col-end-9 flex items-center">
            {t("status.column")}
          </div>
        </div>

        {/* Corps de la table */}
        <div className="w-full">
          {sortedData?.length === 0 || sortedData === undefined ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t("customerHome.noMissionTitle")}</h3>
              <p className="text-gray-500">{t("partner.noMissionText")}</p>
            </div>
          ) : (
            sortedData?.map((mission) => (
              <div
                key={mission?.id}
                className={`w-full hover:bg-blue-50 transition-all duration-200 border-b border-gray-100`}
              >
                <div
                  role="button"
                  tabIndex={!mission?.isAffected && !isSuperAdmin ? -1 : 0}
                  aria-disabled={!mission?.isAffected && !isSuperAdmin}
                  className={`${mission.closed === 1 ? "bg-gray-200" : ""} flex w-full cursor-pointer flex-col gap-3 p-4 text-left transition-colors duration-200 hover:bg-blue-50 md:grid md:grid-cols-9 md:gap-0 ${
                    !mission?.isAffected && !isSuperAdmin ? "cursor-not-allowed opacity-50" : ""
                  }`}
                  onClick={() => {
                    if (mission?.isAffected || isSuperAdmin === 1) {
                      setRenewable(mission?.isRenewable);
                      navigate(
                        `/partner/${idPartner}/customers/${idCustomer}/missions/${mission?.id}/requests`
                      );
                    }
                  }}
                  onKeyDown={(event) => {
                    if (
                      (event.key === "Enter" || event.key === " ") &&
                      (mission?.isAffected || isSuperAdmin === 1)
                    ) {
                      event.preventDefault();
                      setRenewable(mission?.isRenewable);
                      navigate(
                        `/partner/${idPartner}/customers/${idCustomer}/missions/${mission?.id}/requests`
                      );
                    }
                  }}
                >
                  <div className="flex items-center justify-between md:col-start-1 md:col-end-2 md:grid md:grid-cols-2">
                   {/* Notifications */}
                    <button 
                      className="p-2 col-start-1 col-end-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWatchMission(mission?.id, mission?.watching);
                      }}
                      title={mission?.watching ? t("partner.disableNotifs") : t("partner.enableNotifs")}
                    >
                      {mission?.watching ? (
                        <img src={BellActive} alt="Notifications actives" className="w-5 h-5" />
                      ) : (
                        <img src={Bell} alt="Notifications inactives" className="w-5 h-5" />
                      )}
                    </button>
                    <div className="col-start-2 col-end-3 text-sm text-gray-600">
                      {mission?.category} {mission?.nameMissionPeriod}
                    </div>
                  </div>
                  <div className="flex items-center md:col-start-2 md:col-end-4">
                    <div className="font-medium text-gray-900">
                      {mission?.name && splitMissionName(mission?.name)}
                    </div>
                  </div>
                  <div className="flex items-center md:col-start-4 md:col-end-5">
                    <span className="mr-2 text-xs font-semibold text-gray-500 md:hidden">
                      {t("common.manager")}:
                    </span>
                    <div className="text-gray-600 text-sm">
                      {mission?.fullName === " " ? mission.eMail : mission.fullName}
                    </div>
                  </div>
                  <div className="flex items-center md:col-start-5 md:col-end-6">
                    <span className="mr-2 text-xs font-semibold text-gray-500 md:hidden">
                      {t("common.dueDate")}:
                    </span>
                    <div className="text-gray-600 text-sm">
                      {dateFormat(mission?.dueDate)}
                    </div>
                  </div>
                  <div className="hidden items-center md:col-start-6 md:col-end-7 md:flex">
                    <div className="text-gray-600 text-sm">
                      {mission?.archiveDate ? dateFormat(mission?.archiveDate) : ""}
                    </div>
                  </div>
                  <div className="flex items-center md:col-start-7 md:col-end-9">
                    <div className="w-full grid grid-cols-4 gap-2">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2"></div>
                        <span className="text-xs text-gray-600">
                          {mission?.DocRequests[0] &&
                          Object.keys(mission.DocRequests[0])[0] === "En Attente"
                            ? mission.DocRequests[0]["En Attente"]
                            : 0}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                        <span className="text-xs text-gray-600">
                          {mission?.DocRequests[3] &&
                          Object.keys(mission.DocRequests[3])[0] === "En retard"
                            ? mission.DocRequests[3]["En retard"]
                            : 0}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-400 mr-2"></div>
                        <span className="text-xs text-gray-600">
                          {mission?.DocRequests[1] &&
                          Object.keys(mission.DocRequests[1])[0] === "Délivrée"
                            ? mission.DocRequests[1]["Délivrée"]
                            : 0}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-400 mr-2"></div>
                        <span className="text-xs text-gray-600">
                          {mission?.DocRequests[2] &&
                          Object.keys(mission.DocRequests[2])[0] === "Accepté"
                            ? mission.DocRequests[2]["Accepté"]
                            : 0}
                        </span>
                      </div>
                    </div>
                  </div>
                   {/* Actions */}
                  <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3 md:col-start-9 md:col-end-10 md:justify-around md:border-t-0 md:pt-0">
                    {mission?.isRenewable && !mission?.isLastPeriod ? (
                      <button
                        type="button"
                        className="flex items-center justify-center p-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-colors duration-200 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (mission?.isAffected === true) {
                            handleRenewMission(mission.id);
                          }
                        }}
                        disabled={!mission?.isAffected}
                        title={t("partner.renew")}
                      >
                        <img
                          className="w-4 h-4"
                          src={repeat}
                          alt={t("partner.renew")}
                        />
                      </button>
                    ) : (
                      <div className="p-2"></div>
                    )}
                  {(roleId === 1 || roleId === 3 || isSuperAdmin === 1) && mission.closed === 0 &&
                    <>
                    <button
                      type="button"
                      className="flex items-center justify-center p-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-colors duration-200 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (mission?.isAffected === true) {
                          setChoiceModal("update");
                          setMissionId(mission.id);
                        }
                      }}
                      disabled={!mission?.isAffected}
                      title={t("partner.edit")}
                    >
                      <img
                        className="w-4 h-4"
                        src={Pen}
                        alt={t("partner.edit")}
                      />
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center p-2 bg-gray-100 hover:bg-red-100 rounded-lg transition-colors duration-200 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (mission?.isAffected === true) {
                          setMissionId(mission.id);
                          setChoiceModal("archive");
                        }
                      }}
                      disabled={!mission?.isAffected}
                      title={t("partner.archive")}
                    >
                      <img
                        className="w-4 h-4"
                        src={Archive}
                        alt={t("partner.archive")}
                      />
                    </button>
                    </>
                    }
                  </div>          
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerMissionTable;
